import { useState } from "react";
import { Link } from "react-router";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import "../../../i18n";
import { Notice } from "../../../design/components";
import { ApiError } from "../../../api/client";
import { dateTime } from "../../../lib/format";
import type { Entity } from "../../../api/types";
import { familyLabel } from "../../../domain/families";
import { enabledFamiliesOf } from "../../hub/mapping/profileState";
import type { GroupDto, StateBody } from "../../hub/mapping/types";
import { profileVersionQuery, useEntityProfiles, useErpFields } from "../../hub/mapping/useProfileVersion";
import { discoverEveryFamily } from "../../hub/onboard/onboardApi";
import styles from "./connection.module.css";

const LEVELS = ["header", "line", "tax"] as const;

export type MappedSource = { ota: string; title: string; source: string };

/** Both `GET .../erp-fields` and the discovery answer carry these two keys. */
export type DiscoveredField = { name: string; level: string };

/** Every ERP field the profile's rows read, so discovery can say which have gone. */
export function mappedSources(groups: GroupDto[]): MappedSource[] {
  const out: MappedSource[] = [];
  const walk = (group: GroupDto) => {
    for (const row of group.rows) {
      if (row.mode === "field" && row.source) out.push({ ota: row.ota, title: row.title, source: row.source });
    }
    for (const child of group.groups) walk(child);
  };
  groups.forEach(walk);
  return out;
}

/** `null` when nothing can be said yet: no profile read, or discovery found nothing. */
export function orphanedSources(
  state: StateBody | undefined,
  discovered: DiscoveredField[],
): MappedSource[] | null {
  if (!state || discovered.length === 0) return null;
  const names = new Set(discovered.map((field) => field.name));
  return mappedSources(state.groups).filter((row) => !names.has(row.source));
}

function countByLevel(fields: DiscoveredField[], t: TFunction): string {
  return LEVELS.map((level) => [level, fields.filter((field) => field.level === level).length] as const)
    .filter(([, count]) => count > 0)
    .map(([level, count]) => `${count} ${t(`companies.discovery.levels.${level}`, level)}`)
    .join(", ");
}

export function DiscoveryPanel({ entity, canRun }: { entity: Entity; canRun: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fields = useErpFields(entity.id);
  const profiles = useEntityProfiles(entity.id);

  // One (family, profile, version) per enabled family that has a profile; read as one hook, so the
  // hook count never moves with the number of families.
  const byFamily = enabledFamiliesOf(entity.enabledDocTypes).flatMap((family) => {
    const profile = profiles.data?.find((item) => item.family === family);
    return profile ? [{ family, profile, version: profile.activeVersion ?? profile.latestVersion }] : [];
  });
  const states = useQueries({
    queries: byFamily.map((entry) => profileVersionQuery(entry.profile.id, String(entry.version))),
  });

  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [discoveredAt, setDiscoveredAt] = useState<string | null>(null);
  const [discovered, setDiscovered] = useState<DiscoveredField[] | null>(null);

  async function discover(): Promise<void> {
    setRunning(true);
    setError("");
    try {
      // Every enabled family: a purchase family reads different ERP columns, and the
      // Studio gives it no Sales Invoice fallback.
      const perFamily = await discoverEveryFamily(
        entity.id,
        enabledFamiliesOf(entity.enabledDocTypes),
      );
      const last = perFamily[perFamily.length - 1]!;
      setDiscoveredAt(last.discoveredAt);
      setDiscovered(perFamily.flatMap((one) => one.items));
      await queryClient.invalidateQueries({ queryKey: ["entities", entity.id, "erp-fields"] });
    } catch (exc) {
      setError(
        exc instanceof ApiError ? exc.message : t("companies.discovery.readError", "The fields could not be read."),
      );
    } finally {
      setRunning(false);
    }
  }

  const items = fields.data ?? [];

  // `null` per family until both its profile and a discovery answer are in hand.
  const orphansByFamily = byFamily.map((entry, index) => ({
    ...entry,
    orphans: discovered ? orphanedSources(states[index]?.data, discovered) : null,
  }));
  const evaluated = orphansByFamily.filter((entry) => entry.orphans !== null);
  const withOrphans = evaluated.filter((entry) => entry.orphans!.length > 0);

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="grow">{t("companies.discovery.heading", "Field discovery")}</h2>
        <button
          type="button"
          className="btn btn-sm"
          disabled={!canRun || running}
          onClick={() => void discover()}
        >
          {t("companies.discovery.discoverAgain", "Discover again")}
        </button>
      </div>
      <div className="card-body">
        <p className={styles.count}>
          {t("companies.discovery.fieldsDiscovered", "{{count}} fields discovered", { count: items.length })}
        </p>
        {items.length > 0 ? <p className={styles.meta}>{countByLevel(items, t)}</p> : null}
        {discoveredAt ? (
          <p className={styles.meta}>
            {t("companies.discovery.readAt", "Read {{date}}", { date: dateTime(discoveredAt) })}
          </p>
        ) : null}
        {error ? <Notice kind="fail">{error}</Notice> : null}
        {withOrphans.length > 0 ? (
          <Notice kind="warn">
            <p>
              {t(
                "companies.discovery.orphansWarning",
                "These mapped ERP fields are no longer present in the company's ERP:",
              )}
            </p>
            {withOrphans.map((entry) => (
              <div key={entry.family}>
                <h4>{familyLabel(entry.family)}</h4>
                <ul className={styles.orphans}>
                  {entry.orphans!.map((row) => (
                    <li key={row.ota}>
                      <Link
                        className="link"
                        to={`/mapping/${entity.id}/${entry.family}/${entry.version}#row-${row.ota}`}
                      >
                        {row.title}
                      </Link>
                      <span className="mono">{row.source}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Notice>
        ) : null}
        {evaluated.length > 0 && withOrphans.length === 0 ? (
          <Notice kind="ok">
            {t("companies.discovery.allPresent", "Every mapped ERP field is still present.")}
          </Notice>
        ) : null}
      </div>
    </section>
  );
}
